import React from 'react';
import { RiCoinsFill } from 'react-icons/ri';
import { formatCurrency } from '../Utils/Formatters';
import Badge from '../Components/Layout/Badge';
import Button from '../Components/Form/Button';
import { useNavigate } from 'react-router-dom';
import { useStoreHook } from '../Hooks/StoreHook';
import { TbInfoCircle, TbLayoutDashboard } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import { useLayoutContext } from '../Contexts/LayoutContext';

interface FidelityBoxBalanceProps {
    currencyName: string;
    availablePoints: number;
    expiring: number;
    expired: number;
    company: any;
    availableBalance: number;
}

const FidelityBoxBalance = ({ currencyName, availablePoints, expiring, expired, availableBalance }: FidelityBoxBalanceProps) => {

    const navigate = useNavigate();
    const { getLinkToHome } = useStoreHook();
    const { t } = useTranslation();
    const { isMobile, company } = useLayoutContext();

    const handleBackToHome = () => {
        navigate(getLinkToHome());
    }

    return (
        <div className="w-full bg-custom-primary text-white rounded-3xl md:rounded-2xl p-4 md:pl-6 flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4 shadow-lg transition-all">

            <div className="flex flex-col items-center text-center md:items-start">
                <h3 className="text-xl md:text-2xl font-bold">{t('layout.fidelity.balance_title')}</h3>
                <div className="flex items-center">
                    <RiCoinsFill className="text-white text-4xl" />
                    <span className="text-5xl font-bold leading-none zoomInIcon">{availablePoints} </span>
                    <div className="flex flex-col gap-1 items-center">
                        <Badge outline className="ml-1 text-sm px-2 rounded-full fadeInIcon text-white opacity-75 ring-white/30 normal-case">
                            {currencyName}
                        </Badge>
                        <span className="text-xs leading-none opacity-75 fadeInIcon">{formatCurrency(availableBalance)}</span>
                    </div>
                </div>
            </div>

            <hr className="w-full md:hidden border-t border-white/10" />

            <div className="flex flex-col gap-1 flex-1 md:border-l border-white/10">
                <div className="flex flex-col items-center md:items-start md:px-4 py-1">
                    <h4 className="text-sm flex-inline items-center gap-1 font-medium opacity-90 leading-none">
                        <span className='opacity-75'>{t('layout.fidelity.expiring_in_days', { days: 30 })} </span>
                        <span className="text-lg font-extrabold leading-none">{expiring || 0}</span>
                    </h4>
                    <h4 className="text-sm flex-inline items-center gap-1 font-medium opacity-90 leading-none">
                        <span className='opacity-75'>{t('layout.fidelity.expired_in_days', { days: company?.fidelity?.days_to_expire })} </span>
                        <span className="text-lg font-extrabold leading-none">{expired || 0}</span>
                    </h4>
                </div>
                <div className='md:px-3 py-1 pt-2 leading-none flex items-center gap-1 md:border-t border-white/5'>
                    <div className='hidden md:block'><TbInfoCircle size={25} /></div>
                    <div className='flex flex-col items-center md:items-start'>
                        <strong>{t('layout.fidelity.how_to_use')}</strong>
                        <span className='opacity-50'>{t('layout.fidelity.how_to_use_message')}</span>
                    </div>
                </div>
            </div>

            <div className="flex flex-col w-full md:w-fit items-center justify-center w-fit">
                <Button outline={isMobile} onClick={handleBackToHome} className='
                    flex-row md:flex-col gap-1 w-full md:w-fit h-full 
                    md:aspect-square 
                    ring-white/10 md:rounded-xl hover:bg-custom-primary hover:ring-white/25 focus:ring-white/25 hover:bg-black/20 hover:ring-2
                    '>
                    <div><TbLayoutDashboard size={isMobile ? 25 : 35} className='fadeInIcon text-wrap' /></div>
                    {t('menu.mobile.home')}
                </Button>
            </div>
        </div>
    );
};

export default FidelityBoxBalance;
